/**
 * Canonical license-status compliance semantics (PD-14745) — the TypeScript
 * mirror of the mission-control `lm_status_canonical` table, for the frontend +
 * backend consumers that can't join the DB (checkout good-standing gate, list
 * filters, status chips/colors).
 *
 * Source of truth chain: the scripts/license-management classifier
 * (`CANONICAL_STATUS_META` in `lib/license-status.mjs`) seeds the DB table;
 * this constant is the hand-kept TS copy, guarded by
 * `license-status-canonical.test.ts` so the two representations can't drift.
 *
 * Two ORTHOGONAL, hard-boolean flags (a flag IS the impact, and the impact is
 * binary — no null/indeterminate):
 *   canOrder  — is the license valid / in-force, so the holder may place an
 *               order? TRUE only for Active + Probation. This is BOTH the order
 *               gate AND the client-level "covered" test.
 *   isAdverse — did the board take a disciplinary action worth review / watch?
 *               TRUE for Suspended/Revoked/Surrendered/Denied AND Probation
 *               (the orthogonal case: can order, but on watch).
 *
 * Derived, NOT stored: "good standing" = canOrder && !isAdverse. Pending/Unknown
 * are (false, false); their "needs review" meaning is recoverable from the
 * status name, not overloaded into a third flag state.
 */

export const CANONICAL_LICENSE_STATUSES = [
	'Active',
	'Expired',
	'Inactive',
	'Pending',
	'Probation',
	'Suspended',
	'Revoked',
	'Surrendered',
	'Denied',
	'Cancelled',
	'Deceased',
	'Unknown',
] as const;

export type CanonicalLicenseStatus =
	(typeof CANONICAL_LICENSE_STATUSES)[number];

export interface CanonicalStatusFlagsModel {
	canOrder: boolean;
	isAdverse: boolean;
}

export const CANONICAL_STATUS_META: Record<
	CanonicalLicenseStatus,
	CanonicalStatusFlagsModel
> = {
	Active: { canOrder: true, isAdverse: false },
	Probation: { canOrder: true, isAdverse: true },
	Suspended: { canOrder: false, isAdverse: true },
	Revoked: { canOrder: false, isAdverse: true },
	Surrendered: { canOrder: false, isAdverse: true },
	Denied: { canOrder: false, isAdverse: true },
	Expired: { canOrder: false, isAdverse: false },
	Inactive: { canOrder: false, isAdverse: false },
	Cancelled: { canOrder: false, isAdverse: false },
	Deceased: { canOrder: false, isAdverse: false },
	Pending: { canOrder: false, isAdverse: false },
	Unknown: { canOrder: false, isAdverse: false },
};

/** Canonical statuses whose holder may place an order (Active, Probation). */
export const CAN_ORDER_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter((s) => CANONICAL_STATUS_META[s].canOrder);

/** Canonical statuses under a board action worth review/watch. */
export const ADVERSE_STATUSES: ReadonlyArray<CanonicalLicenseStatus> =
	CANONICAL_LICENSE_STATUSES.filter((s) => CANONICAL_STATUS_META[s].isAdverse);

// Case-insensitive lookup: registry statuses arrive in canonical case, but
// tenant-mirrored / legacy values can vary in case, so normalize before lookup.
const FLAGS_BY_LOWER: Record<string, CanonicalStatusFlagsModel> =
	Object.fromEntries(
		Object.entries(CANONICAL_STATUS_META).map(([k, v]) => [k.toLowerCase(), v]),
	);

/**
 * Is a license under this status valid/in-force so the holder may order?
 * Unknown/unmapped/null statuses return `false` (conservative — never assume
 * order eligibility from a status we don't recognize).
 */
export function statusCanOrder(status: string | null | undefined): boolean {
	if (status == null) {
		return false;
	}
	return FLAGS_BY_LOWER[status.toLowerCase()]?.canOrder === true;
}

/**
 * Is a license under this status flagged with a board action worth review/watch?
 * Unknown/unmapped/null statuses return `false`.
 */
export function statusIsAdverse(status: string | null | undefined): boolean {
	if (status == null) {
		return false;
	}
	return FLAGS_BY_LOWER[status.toLowerCase()]?.isAdverse === true;
}
